import { useState } from 'react'
import DataTable from '../../components/DataTable'
import StatsCard from '../../components/StatsCard'
import { mockCareMarketplace } from '../../data/mockData'
import { Star, ShoppingCart, TrendingUp, Award } from 'lucide-react'

export default function CareMarketplacePage() {
  const [sortKey, setSortKey] = useState('sales')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filterCategory, setFilterCategory] = useState('All')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  // Filter items
  let filteredItems = mockCareMarketplace
  if (filterCategory !== 'All') {
    filteredItems = filteredItems.filter((item) => item.category === filterCategory)
  }

  const totalRevenue = mockCareMarketplace.reduce((sum, item) => sum + (item.price * item.sales), 0)
  const totalSales = mockCareMarketplace.reduce((sum, item) => sum + item.sales, 0)
  const avgRating = (mockCareMarketplace.reduce((sum, item) => sum + item.rating, 0) / mockCareMarketplace.length).toFixed(1)

  // Category breakdown
  const categories = [...new Set(mockCareMarketplace.map((item) => item.category))]
  const categoryStats = categories.map((cat) => {
    const items = mockCareMarketplace.filter((item) => item.category === cat)
    return {
      name: cat,
      count: items.length,
      sales: items.reduce((sum, item) => sum + item.sales, 0),
      revenue: items.reduce((sum, item) => sum + (item.price * item.sales), 0),
    }
  })

  const columns = [
    { key: 'name', label: 'Product/Service', sortable: true },
    { key: 'seller', label: 'Provider', sortable: false },
    { key: 'category', label: 'Category', sortable: true },
    {
      key: 'price',
      label: 'Price (AED)',
      sortable: true,
      render: (value) => `AED ${value}`,
    },
    {
      key: 'rating',
      label: 'Rating',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1">
          <Star size={14} className="text-yellow-400 fill-yellow-400" />
          <span className="text-sm font-medium">{value}</span>
        </div>
      ),
    },
    { key: 'sales', label: 'Sales', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">{value}</span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Care Marketplace</h1>
        <p className="text-pawssible-slate-600 mt-1">Pet care products, services & provider performance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Total Listings"
          value={mockCareMarketplace.length}
          icon={ShoppingCart}
          color="teal"
        />
        <StatsCard
          label="Total Sales"
          value={totalSales.toLocaleString()}
          icon={TrendingUp}
          color="amber"
        />
        <StatsCard
          label="Total Revenue"
          value={`AED ${(totalRevenue / 1000).toFixed(1)}k`}
          icon={Award}
          color="teal"
        />
        <StatsCard
          label="Avg Rating"
          value={avgRating}
          icon={Star}
          color="amber"
        />
      </div>

      {/* Category Performance */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Category Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categoryStats.map((cat) => (
            <div key={cat.name} className="border border-pawssible-slate-200 rounded-lg p-4">
              <p className="font-semibold text-pawssible-slate-900">{cat.name}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-pawssible-slate-600">Listings</span>
                  <span className="font-semibold text-pawssible-slate-900">{cat.count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-pawssible-slate-600">Total Sales</span>
                  <span className="font-semibold text-pawssible-slate-900">{cat.sales}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-pawssible-slate-600">Revenue</span>
                  <span className="font-semibold text-pawssible-teal-600">AED {(cat.revenue / 1000).toFixed(1)}k</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter Controls */}
      <div className="card">
        <label className="label">Filter by Category</label>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="input"
        >
          <option>All</option>
          {categories.map((cat) => (
            <option key={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Add Listing</button>
        <button className="btn-secondary">Manage Disputes</button>
        <button className="btn-secondary">View Analytics</button>
      </div>

      {/* Listings Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">All Listings</h2>
        <DataTable
          columns={columns}
          data={filteredItems}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
        />
      </div>
    </div>
  )
}
